import { registerPlugin, Capacitor } from '@capacitor/core';

/**
 * Pushes the live yardage out to the iOS home screen and CarPlay dashboard.
 *
 * GoCaddy cannot be a CarPlay app — Apple issues the entitlement only for a
 * closed list of categories and golf is not among them — so a widget is the
 * only surface that reaches a buggy's screen. From iOS 26 the system places any
 * widget supporting the small family onto the CarPlay dashboard without an
 * entitlement of any kind.
 *
 * Everything is computed in the app and handed over finished. The widget does
 * no maths and takes no GPS fix of its own: a widget that woke the location
 * stack would drain the phone across a four-hour round for a number this side
 * already knows.
 */

export interface YardagePayload {
  course: string;
  hole: number;
  par: number;
  /** Distance to the middle of the green, metres. The widget converts. */
  metres: number;
  unit: 'm' | 'yd';
  /** Geometry for the map tile. Omitted when there is no fix yet. */
  playerLat?: number;
  playerLng?: number;
  teeLat?: number;
  teeLng?: number;
  greenLat?: number;
  greenLng?: number;
  /** m/s and degrees true. Drive the road view's zoom and rotation. */
  speed?: number;
  heading?: number;
}

interface YardageWidgetPlugin {
  update(payload: YardagePayload): Promise<void>;
  diag(payload: Record<string, unknown>): Promise<void>;
  clear(): Promise<void>;
}

const plugin = registerPlugin<YardageWidgetPlugin>('YardageWidget');

/** Only iOS carries the widget; everywhere else this is a no-op. */
export function widgetSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

/**
 * Rounded before comparing, because a GPS fix jitters constantly and an
 * unrounded value would fire a native call several times a second for a tile
 * that renders identically.
 *
 * Position is rounded to ~1 m (5 decimal places) rather than to the metre of
 * distance: the map tile moves the golfer's dot even when the yardage to the
 * green has not changed, which happens whenever they walk across the hole
 * rather than up it.
 */
let last = '';
let lastAt = 0;

/**
 * Floor on how often a push happens.
 *
 * A widget normally gets 72 reloads a day — one every 3.3 minutes across a
 * four-hour round, which would be useless. But reloads are BUDGET-FREE while
 * the app is using the standard location service, which is exactly what
 * GoCaddy is doing during a round. So the ceiling here is WidgetKit throttling,
 * not the daily budget, and 15s is a throttle guard rather than a ration.
 *
 * See docs/MERCURY_FAIRWAY_WIDGET_LIMITS.md.
 */
const MIN_INTERVAL_MS = 15_000;

/**
 * Buggy pace gets the tight cadence.
 *
 * A buggy does about 15 km/h — 4.2 m/s. At a 15s floor that is 62 m between
 * redraws, which is a dot that teleports past three tee boxes. At 5s it is
 * 21 m, which reads as movement. This is the speed the product is actually
 * used at, so it gets the budget.
 *
 * Road speed does NOT need it: at 60 km/h the map has zoomed out to 1.6 km
 * across, so 12s of travel is a small fraction of the tile and pushing harder
 * buys nothing but battery. Reloads are budget-free while location is running
 * (see MERCURY_FAIRWAY_WIDGET_LIMITS.md), so the only cost here is power.
 */
const SLOW_MS_PER_S = 8;          // ~29 km/h — above this, treat it as a road
const SLOW_INTERVAL_MS = 5_000;
const SLOW_MOVE_M = 3;

/**
 * Push even when standing still, so a stationary tile never looks frozen.
 *
 * Movement alone is not enough: a golfer waiting on a tee for five minutes
 * would watch the dashboard go stale behind them, and "not live" on a tile the
 * customer is being shown is worse than a number that has not changed.
 */
const HEARTBEAT_MS = 45_000;

/** A yardage that has not moved this far renders identically. */
const MIN_MOVE_M = 10;

/** Cheap metres between two lat/lngs — exact enough to decide "has it moved". */
function movedM(aLat?: number, aLng?: number, bLat?: number, bLng?: number): number {
  if (aLat == null || aLng == null || bLat == null || bLng == null) return Infinity;
  const dLat = (aLat - bLat) * 111_132;
  const dLng = (aLng - bLng) * 111_320 * Math.cos((aLat * Math.PI) / 180);
  return Math.hypot(dLat, dLng);
}

let lastPos: { lat?: number; lng?: number } = {};

export async function pushYardage(p: YardagePayload): Promise<void> {
  if (!widgetSupported()) return;

  // Identity of the tile's content, ignoring position — a hole or distance
  // change must go through immediately regardless of the rate limit.
  const key = [p.course, p.hole, p.par, Math.round(p.metres), p.unit,
    // Banded, so a rezoom fires when the vehicle genuinely changes pace
    // rather than on every tremor in the speed reading.
    Math.round((p.speed ?? 0) / 3)].join('|');
  const moved = movedM(p.playerLat, p.playerLng, lastPos.lat, lastPos.lng);
  const now = Date.now();

  const slow = (p.speed ?? 0) < SLOW_MS_PER_S;
  const interval = slow ? SLOW_INTERVAL_MS : MIN_INTERVAL_MS;
  const minMove = slow ? SLOW_MOVE_M : MIN_MOVE_M;

  const contentChanged = key !== last;
  const movedEnough = moved >= minMove && now - lastAt >= interval;
  const heartbeat = now - lastAt >= HEARTBEAT_MS;
  if (!contentChanged && !movedEnough && !heartbeat) return;

  last = key;
  lastAt = now;
  lastPos = { lat: p.playerLat, lng: p.playerLng };
  try {
    await plugin.update({ ...p, metres: Math.round(p.metres) });
    lastError = null;
    pushes += 1;
  } catch (e) {
    // Swallowing this was a mistake: when the native plugin was not reachable
    // at all, every push failed silently and the tile sat on its empty state
    // looking like a data problem. The map must still survive a widget
    // failure, but the failure has to be inspectable.
    lastError = (e as Error)?.message ?? String(e);
  }
}

/** Last push failure, or null. Read by the shell to show what went wrong. */
let lastError: string | null = null;
export const widgetError = () => lastError;
/** Pushes that have actually reached the native side. */
export const widgetPushCount = () => pushes;
let pushes = 0;

/**
 * Heartbeat the GPS's health into the shared container.
 *
 * Separate from pushYardage on purpose: that one is gated on having a fix, and
 * the failure worth diagnosing is having none. This runs regardless, so a phone
 * locked on a desk can still be asked what its watchers are doing.
 */
export async function pushDiag(payload: Record<string, unknown>): Promise<void> {
  if (!widgetSupported()) return;
  try {
    await plugin.diag(payload);
  } catch (e) {
    lastError = (e as Error)?.message ?? String(e);
  }
}

export async function clearYardage(): Promise<void> {
  if (!widgetSupported()) return;
  last = '';
  try {
    await plugin.clear();
  } catch {
    /* see above */
  }
}
